import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  TrendingUp,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { 
  getDailyRates, 
  createDailyRate, 
  updateDailyRate, 
  deleteDailyRate 
} from '../redux/actions/dailyRateActions';

const DailyRates = () => {
  const dispatch = useDispatch();
  const { dailyRates, pagination, loading, error } = useSelector((state) => state.dailyRates);

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRate, setSelectedRate] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [formData, setFormData] = useState({
    rate_date: '',
    gold_rate: '',
    silver_rate: '',
    diamond_rate: ''
  });

  useEffect(() => {
    dispatch(getDailyRates({ page: page + 1, limit: rowsPerPage, search: searchTerm }));
  }, [dispatch, page, rowsPerPage, searchTerm]);

  const handleOpenDialog = (rate = null) => {
    if (rate) {
      setSelectedRate(rate);
      setIsEdit(true);
      setFormData({
        rate_date: rate.rate_date,
        gold_rate: rate.gold_rate.toString(),
        silver_rate: rate.silver_rate.toString(),
        diamond_rate: rate.diamond_rate.toString()
      });
    } else {
      setSelectedRate(null);
      setIsEdit(false);
      setFormData({
        rate_date: new Date().toISOString().split('T')[0],
        gold_rate: '',
        silver_rate: '',
        diamond_rate: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedRate(null);
    setIsEdit(false);
    setFormData({
      rate_date: '',
      gold_rate: '',
      silver_rate: '',
      diamond_rate: ''
    });
  };

  const handleSubmit = async () => {
    try {
      if (isEdit) {
        await dispatch(updateDailyRate(selectedRate.id, formData));
      } else {
        await dispatch(createDailyRate(formData));
      }
      handleCloseDialog();
      dispatch(getDailyRates({ page: page + 1, limit: rowsPerPage, search: searchTerm }));
    } catch (error) {
      console.error('Error saving daily rate:', error);
    }
  };

  const handleDelete = async (rateId) => {
    if (window.confirm('Are you sure you want to delete this daily rate?')) {
      try {
        await dispatch(deleteDailyRate(rateId));
        dispatch(getDailyRates({ page: page + 1, limit: rowsPerPage, search: searchTerm }));
      } catch (error) {
        console.error('Error deleting daily rate:', error);
      }
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Daily Rate Master
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Daily Rate
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Search sx={{ mr: 1, color: 'text.secondary' }} />
            <TextField
              placeholder="Search by date..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ minWidth: 300 }}
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Gold Rate (₹)</TableCell>
                  <TableCell align="right">Silver Rate (₹)</TableCell>
                  <TableCell align="right">Diamond Rate (₹)</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : dailyRates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No daily rates found
                    </TableCell>
                  </TableRow>
                ) : (
                  dailyRates.map((rate) => (
                    <TableRow key={rate.id}>
                      <TableCell>
                        {new Date(rate.rate_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          {formatCurrency(rate.gold_rate)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold" color="secondary">
                          {formatCurrency(rate.silver_rate)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          {formatCurrency(rate.diamond_rate)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(rate)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(rate.id)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={pagination?.total || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEdit ? 'Edit Daily Rate' : 'Add Daily Rate'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Rate Date"
                type="date"
                value={formData.rate_date}
                onChange={(e) => setFormData({ ...formData, rate_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Gold Rate (₹)"
                type="number"
                value={formData.gold_rate}
                onChange={(e) => setFormData({ ...formData, gold_rate: e.target.value })}
                inputProps={{ step: "0.01" }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Silver Rate (₹)"
                type="number"
                value={formData.silver_rate}
                onChange={(e) => setFormData({ ...formData, silver_rate: e.target.value })}
                inputProps={{ step: "0.01" }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Diamond Rate (₹)"
                type="number"
                value={formData.diamond_rate}
                onChange={(e) => setFormData({ ...formData, diamond_rate: e.target.value })}
                inputProps={{ step: "0.01" }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {isEdit ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DailyRates;

